import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Phone, MapPin, MessageCircle, ArrowRight, Sparkles, Calendar, Wrench, LifeBuoy } from "lucide-react";
import Reveal from "../components/Reveal";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { BRAND, WHATSAPP_LINK } from "../lib/brand";
import emailjs from "@emailjs/browser";

const ENQ_TYPES = ["Software Demo Request", "Hardware Purchase", "Technical Support", "Other"];

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

export default function Contact() {
  const [form, setForm] = useState({ name: "", biz: "", phone: "", email: "", type: "", msg: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.biz || !form.phone) {
      toast.error("Please fill in name, business and phone.");
      return;
    }
    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      toast.error("Email service is not configured. Please try again later.");
      return;
    }

    setSubmitting(true);

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          name: form.name,
          company: form.biz,
          phone: form.phone,
          email: form.email || "Not provided",
          message: form.msg || "No message provided",
          time: new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }),
        },
        { publicKey: EMAILJS_PUBLIC_KEY },
      );

      setSubmitting(false);
      toast.success("Enquiry sent! Our team will reach out within 24 hours.");
      setForm({ name: "", biz: "", phone: "", email: "", type: "", msg: "" });
    } catch (error) {
      setSubmitting(false);
      toast.error("Failed to send enquiry. Please try again.");
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative gi-hero-gradient text-white pt-32 pb-20">
        <div className="absolute inset-0 gi-mesh opacity-40" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 text-center">
          <Reveal>
            <div className="flex justify-center items-center gap-2 text-white/60 text-sm mb-4">
              <Link to="/" className="hover:text-white">Home</Link><ChevronRight className="h-3 w-3" /><span className="text-white">Contact Us</span>
            </div>
            <span className="gi-pill-dark"><Sparkles className="h-3.5 w-3.5" /> Get in Touch</span>
            <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl mt-5 leading-tight">Let&apos;s talk about your business</h1>
            <p className="text-white/75 mt-5 max-w-2xl mx-auto text-lg">
              Request a demo, ask about pricing or just say hello. Our team is ready to help.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white gi-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* FORM */}
          <Reveal className="lg:col-span-7">
            <form data-testid="contact-form" onSubmit={onSubmit} className="gi-card p-7 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Full Name *" testId="contact-name"><Input data-testid="contact-name-input" value={form.name} onChange={onChange("name")} placeholder="Your full name" /></Field>
              <Field label="Business Name *" testId="contact-biz"><Input data-testid="contact-biz-input" value={form.biz} onChange={onChange("biz")} placeholder="Business name" /></Field>
              <Field label="Phone Number *" testId="contact-phone"><Input data-testid="contact-phone-input" value={form.phone} onChange={onChange("phone")} placeholder="+91 99959 89843" /></Field>
              <Field label="Email Address" testId="contact-email"><Input data-testid="contact-email-input" type="email" value={form.email} onChange={onChange("email")} placeholder="you@business.com" /></Field>
              <Field label="Enquiry Type" testId="contact-type" className="md:col-span-2">
                <Select value={form.type} onValueChange={onChange("type")}>
                  <SelectTrigger data-testid="contact-type-trigger"><SelectValue placeholder="Select an enquiry type" /></SelectTrigger>
                  <SelectContent>
                    {ENQ_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Message" testId="contact-msg" className="md:col-span-2">
                <Textarea data-testid="contact-msg-input" rows={5} value={form.msg} onChange={onChange("msg")} placeholder="How can we help?" />
              </Field>
              <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs text-[#6B7280]">We&apos;ll get back to you within 24 hours.</p>
                <button type="submit" data-testid="contact-submit" disabled={submitting} className="gi-btn-primary disabled:opacity-70">
                  {submitting ? "Sending…" : "Send Enquiry"} <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </form>
          </Reveal>

          {/* INFO */}
          <Reveal delay={120} className="lg:col-span-5">
            <div className="rounded-2xl overflow-hidden border border-[#DDE6F0] shadow-[0_8px_30px_rgba(11,37,69,0.10)] bg-gradient-to-br from-[#0B2545] to-[#1565C0] text-white p-8">
              <div className="font-display font-bold text-3xl leading-tight">{BRAND.phone1}</div>
              <div className="text-white/70 text-sm mt-1">Primary line — 9 AM to 7 PM IST</div>
              <div className="font-display font-bold text-2xl mt-5">{BRAND.phone2}</div>
              <div className="text-white/70 text-sm mt-1">Alternate line</div>

              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-btn" className="gi-btn-whatsapp mt-7">
                <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
              </a>

              <div className="mt-7 pt-6 border-t border-white/10 space-y-3 text-sm text-white/85">
                <div className="flex items-start gap-3"><MapPin className="h-4 w-4 mt-0.5 text-[#00B4D8] shrink-0" /> {BRAND.address}</div>
                <div className="flex items-center gap-3"><Phone className="h-4 w-4 text-[#00B4D8]" /> {BRAND.phone1} · {BRAND.phone2}</div>
                <div className="text-xs text-white/60">GST No: {BRAND.gst}</div>
              </div>
            </div>

            {/* Map */}
            <div className="mt-6 rounded-2xl overflow-hidden border border-[#DDE6F0] shadow-lg">
              <iframe
                title="Gravityinc Office Map"
                src="https://www.google.com/maps?q=Panampilly+Nagar+Kochi+682036&output=embed"
                width="100%"
                height="260"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#F5F8FF] gi-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <h2 className="font-display font-bold text-3xl md:text-4xl mb-3 text-center">What to Expect</h2>
            <p className="text-[#6B7280] text-center max-w-2xl mx-auto">Here&apos;s how working with us looks, from first call to long-term support.</p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Calendar, title: "Free Demo", text: "We'll walk you through the right DigiPOS product for your business, live." },
              { icon: Wrench, title: "Quick Implementation", text: "Our team handles setup, installation and staff training end-to-end." },
              { icon: LifeBuoy, title: "Ongoing Support", text: "After-sales support is part of every package we offer." },
            ].map((c, i) => (
              <Reveal key={c.title} delay={i * 80}>
                <div className="gi-card gi-card-hover p-7 text-center h-full">
                  <div className="h-14 w-14 rounded-2xl mx-auto bg-gradient-to-br from-[#1565C0] to-[#00B4D8] text-white grid place-items-center mb-4">
                    <c.icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-display font-semibold text-lg">{c.title}</h3>
                  <p className="text-sm text-[#6B7280] mt-2">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, children, testId, className = "" }) => (
  <div className={className}>
    <Label className="text-xs uppercase tracking-wider font-medium text-[#6B7280] mb-2 block">{label}</Label>
    <div data-testid={testId}>{children}</div>
  </div>
);
